import { useState } from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Avatar } from '@/components/ui/avatar'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { ExternalLink, Users, Trash2, Calendar, BarChart3, MoreHorizontal, RefreshCw } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { formatFollowers, formatDate } from '@/utils/saved-analytics'
import { useNavigate } from 'react-router-dom'
import { useToast } from '@/hooks/use-toast'
import { refreshAnalyticsProfile } from '@/utils/analytics-refresh'

interface SavedInfluencerCardProps {
  id?: string
  title: string
  snippet: string
  followers: number
  url: string
  savedDate?: string
  daysAnalyzed?: number
  onRemove?: () => void
  onDelete?: () => void // For analyzed profiles
  isAnalyzed?: boolean // To determine styling and badge
}

export function SavedInfluencerCard({ 
  id,
  title, 
  snippet, 
  followers, 
  url, 
  savedDate = "2024-01-15",
  daysAnalyzed,
  onRemove,
  onDelete,
  isAnalyzed = false
}: SavedInfluencerCardProps) {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [isRefreshing, setIsRefreshing] = useState(false)



  const extractUsername = (title: string, url: string) => {
    // Try to extract username from URL first
    const urlParts = url.split('/')
    const usernameFromUrl = urlParts[urlParts.length - 1] || urlParts[urlParts.length - 2]
    if (usernameFromUrl && usernameFromUrl !== 'instagram.com' && usernameFromUrl.length > 0) {
      return usernameFromUrl.replace(/[^a-zA-Z0-9_]/g, '')
    }
    
    // Fallback to extracting from title
    const cleanedTitle = title.toLowerCase().replace(/[^a-z0-9_\s]/g, '').trim()
    const words = cleanedTitle.split(' ').filter(word => word.length > 0)
    
    // Look for username-like patterns (no spaces, reasonable length)
    for (const word of words) {
      if (word.length >= 2 && word.length <= 20 && !['instagram', 'photos', 'videos', 'account', 'profile'].includes(word)) {
        return word
      }
    }
    
    // Final fallback - use first word or first 2 characters
    return words[0] || title.slice(0, 2)
  }

  const username = extractUsername(title, url)
  const initials = username.slice(0, 2).toUpperCase()

  const trimSnippet = (text: string, maxLength: number = 120) => {
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength).trim() + '...'
  }

  const handleViewAnalytics = () => {
    if (id) {
      navigate(`/instagram/account/${id}`)
    }
  }

  const handleRefresh = async () => {
    if (!id || isRefreshing) return

    setIsRefreshing(true)
    
    try {
      const result = await refreshAnalyticsProfile(id)
      
      if (result.success) {
        toast({
          title: 'Refresh Started',
          description: result.message,
        })
      } else {
        toast({
          title: 'Refresh Failed',
          description: result.message,
          variant: 'destructive'
        })
      }
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.response?.data?.message || error?.message
      if (error?.response?.status === 400 && typeof detail === 'string' && detail.includes('No available analytics remaining')) {
        toast({ title: 'Limit reached', description: 'No available analytics remaining.', variant: 'destructive' })
      } else {
        console.error('Failed to refresh analytics:', error)
        toast({
          title: 'Error',
          description: 'Failed to refresh analytics. Please try again.',
          variant: 'destructive'
        })
      }
    } finally {
      setIsRefreshing(false)
    }
  }

  return (
    <TooltipProvider>
      <motion.div
        whileHover={{ y: -2 }}
        transition={{ duration: 0.2 }}
      >
        <Card className={`group hover:shadow-md transition-all duration-200 ${
          isAnalyzed 
            ? 'border border-green-200/20 dark:border-green-800/20' 
            : 'border border-purple-200/20 dark:border-purple-800/20'
        }`}>
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <Avatar className="h-10 w-10 flex-shrink-0">
                  <div className={`w-full h-full ${
                    isAnalyzed 
                      ? 'bg-gradient-to-br from-green-400 to-blue-400' 
                      : 'bg-gradient-to-br from-purple-400 to-pink-400'
                  } flex items-center justify-center text-white text-sm font-semibold`}>
                    {initials}
                  </div>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-base font-semibold group-hover:text-primary transition-colors truncate">
                      {title}
                    </CardTitle>
                    {isAnalyzed && (
                      <Badge variant="secondary" className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 shrink-0">
                        Analyzed
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      <span className="font-medium">{formatFollowers(followers)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      <span>{formatDate(savedDate)}</span>
                    </div>
                    {daysAnalyzed && (
                      <div className="flex items-center gap-1">
                        <BarChart3 className="h-3 w-3" />
                        <span>{daysAnalyzed}d analysis</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0 flex-shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreHorizontal className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {id && (
                    <DropdownMenuItem 
                      onSelect={(e) => {
                        if (isRefreshing) {
                          e.preventDefault()
                          return
                        }
                        e.preventDefault()
                        handleRefresh()
                      }}
                      disabled={isRefreshing}
                    >
                      <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                      {isRefreshing ? 'Refreshing...' : 'Refresh'}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem 
                    onClick={isAnalyzed ? onDelete : onRemove}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {isAnalyzed ? 'Delete' : 'Remove'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mt-2">
              {trimSnippet(snippet)}
            </p>
          </CardHeader>
          
          <CardContent className="pt-0">
            <div className="flex gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button asChild size="sm" variant="outline" className="flex-1">
                    <a href={url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="mr-2 h-3 w-3" />
                      View
                    </a>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>View Instagram profile</p>
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="flex-1"
                    onClick={handleViewAnalytics}
                    disabled={!id}
                  >
                    <BarChart3 className="mr-2 h-3 w-3" />
                    Analytics
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{id ? 'View Analytics' : 'Request Analytics'}</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </TooltipProvider>
  )
} 